// Demo tenants: build one from a folder, and take it apart a row at a time.
import usePageTitle from "../utils/usePageTitle";
import React, { useCallback, useEffect, useState } from "react";
import { Container } from "common-tailwind-components";
import { Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DemoContents from "../components/demo/DemoContents";
import DemoImport from "../components/demo/DemoImport";
import { type Directory, loadDirectory } from "../components/demo/directory";
import { useLogin } from "../reducers";
import { isAdmin } from "../utils/permissions";

const DemoUserImport = () => {
  const { t } = useTranslation();
  const { permissions } = useLogin();
  const [directory, setDirectory] = useState<Directory | null>(null);

  usePageTitle(t("tour.demo_user_import"));
  const admin = isAdmin(permissions);

  const reload = useCallback(() => {
    // The directory only feeds the contents panel: failing to load it costs that, not the import.
    loadDirectory()
      .then(setDirectory)
      .catch(() => setDirectory(null));
  }, []);

  useEffect(() => {
    if (admin) {
      reload();
    }
  }, [admin, reload]);

  if (!admin) {
    return (
      <Container>
        <div className="mx-auto max-w-4xl px-4 py-8">
          <Link
            to="/administration/"
            className="inline-flex text-sm text-primary transition hover:text-primary/80"
          >
            {t("back_to_executive_dashboard")}
          </Link>
          <div className="mt-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {t("permission_denied_reason.admin_required")}
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-8">
        <div className="space-y-2">
          <Link
            to="/administration/"
            className="inline-flex text-sm text-primary transition hover:text-primary/80"
          >
            {t("back_to_executive_dashboard")}
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-info/25 bg-info/10 text-info">
              <Users className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-text">{t("tour.demo_user_import")}</h1>
              <p className="text-sm text-text-dim">{t("tour.demo_user_import_description")}</p>
            </div>
          </div>
        </div>

        <DemoImport onImported={reload} />
        {directory && <DemoContents directory={directory} onChanged={reload} />}
      </div>
    </Container>
  );
};

export default DemoUserImport;
